import { ChevronRight, Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { StudioTeamRoleKey } from '@/api/adminClient';
import { Button } from '@/components/ui/button';

import { type PickerLocks, RolePicker } from './RolePicker';
import { type RoleCatalog, effectivePermissionLabels, redundantNotes, redundantRoles, roleLabel, roleSections } from './usersModel';

const BUNDLE_GROUPS = [{ key: 'bundles', label: 'Role' }];
const PLATFORM_GROUPS = [{ key: 'administration', label: 'Platform' }];

/** What the picked roles add up to, as one line of permission names. */
export function EffectivePermissions({ catalog, selected }: { catalog: RoleCatalog; selected: Iterable<StudioTeamRoleKey> }) {
  const labels = effectivePermissionLabels(catalog, selected);
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-xs leading-5" aria-label="Effective permissions">
      <p className="font-semibold uppercase tracking-wide text-muted-foreground">Effective permissions</p>
      <p className="text-foreground">{labels.length > 0 ? labels.join(' · ') : 'None'}</p>
    </div>
  );
}

/**
 * Picking Studio roles the recommended way: a RapidSOS bundle first, Platform
 * Admin on its own, and the single permissions folded under Advanced. Singles
 * a picked bundle already grants are marked, with "Clean up" to drop them.
 * Used by Edit roles and Invite alike.
 */
export function RoleSections({
  catalog,
  selected,
  onToggle,
  onCleanUp,
  locks = {},
  disabled = false,
  advancedOpen,
}: {
  catalog: RoleCatalog;
  selected: ReadonlySet<StudioTeamRoleKey>;
  onToggle: (key: StudioTeamRoleKey, checked: boolean) => void;
  onCleanUp: () => void;
  locks?: PickerLocks;
  disabled?: boolean;
  /** Start with Advanced open, e.g. when the person already holds a single permission. */
  advancedOpen?: boolean;
}) {
  const { bundles, platformAdmin, advanced } = roleSections(catalog);
  const [open, setOpen] = useState(Boolean(advancedOpen));
  const redundant = redundantRoles(catalog, selected);
  const pickedSingles = advanced.filter((role) => selected.has(role.key)).length;

  return (
    <div className="space-y-5">
      <RolePicker roles={bundles} groups={BUNDLE_GROUPS} selected={selected} onToggle={onToggle} locks={locks} disabled={disabled} />

      {redundant.length > 0 ? (
        <div role="status" className="flex flex-col gap-2 rounded-lg border border-warning/30 bg-warning-bg px-3 py-2.5 text-xs leading-5 sm:flex-row sm:items-center">
          <Sparkles className="hidden h-4 w-4 shrink-0 text-warning-foreground sm:block" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-foreground">
            {redundant.map((entry) => roleLabel(catalog, entry.key)).join(', ')} {redundant.length === 1 ? 'is' : 'are'} already included in{' '}
            {[...new Set(redundant.map((entry) => roleLabel(catalog, entry.coveredBy)))].join(', ')}.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={onCleanUp} disabled={disabled} className="shrink-0">
            Clean up
          </Button>
        </div>
      ) : null}

      {platformAdmin ? (
        <RolePicker roles={[platformAdmin]} groups={PLATFORM_GROUPS} selected={selected} onToggle={onToggle} locks={locks} disabled={disabled} />
      ) : null}

      <EffectivePermissions catalog={catalog} selected={selected} />

      {advanced.length > 0 ? (
        <div className="rounded-lg border border-border">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted/40"
          >
            <ChevronRight className={open ? 'h-4 w-4 rotate-90 transition-transform' : 'h-4 w-4 transition-transform'} aria-hidden="true" />
            <span className="flex-1">Advanced: individual permissions</span>
            {pickedSingles > 0 ? <span className="text-xs font-normal text-muted-foreground">{pickedSingles} picked</span> : null}
          </button>
          {open ? (
            <div className="border-t border-border px-3 py-3">
              <p className="mb-3 text-xs text-muted-foreground">For exceptions. A role above is the usual way to give access.</p>
              <RolePicker
                roles={advanced}
                groups={catalog.groups.filter((group) => group.key !== 'bundles')}
                selected={selected}
                onToggle={onToggle}
                locks={locks}
                notes={redundantNotes(catalog, selected)}
                disabled={disabled}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
